import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText,
  Download,
  Calendar,
  TrendingUp,
  DollarSign,
  Users,
  Briefcase,
  Clock,
  PieChart,
  BarChart3,
  FileSpreadsheet,
  Filter,
  Printer,
  RefreshCw,
  Shield,
  Receipt
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';

const reportTypes = [
  {
    id: 'financial_summary',
    name: 'Financial Summary',
    description: 'Revenue, expenses, and profit/loss overview',
    icon: DollarSign,
    category: 'financial',
    color: 'green'
  },
  {
    id: 'accounts_receivable',
    name: 'Accounts Receivable',
    description: 'Outstanding invoices and aging analysis',
    icon: Receipt,
    category: 'financial',
    color: 'amber'
  },
  {
    id: 'trust_account',
    name: 'Trust Account Report',
    description: 'IOLTA balances and transactions',
    icon: Shield,
    category: 'compliance',
    color: 'purple'
  },
  {
    id: 'case_status',
    name: 'Case Status Report',
    description: 'Active, closed, and pending cases',
    icon: Briefcase,
    category: 'operational',
    color: 'blue'
  },
  {
    id: 'time_summary',
    name: 'Time Summary',
    description: 'Billable hours by attorney and matter',
    icon: Clock,
    category: 'operational',
    color: 'orange'
  },
  {
    id: 'client_activity',
    name: 'Client Activity',
    description: 'Client matters, billing, and communication',
    icon: Users,
    category: 'operational',
    color: 'indigo'
  },
  {
    id: 'revenue_by_practice',
    name: 'Revenue by Practice Area',
    description: 'Revenue breakdown by legal specialty',
    icon: PieChart,
    category: 'financial',
    color: 'pink'
  },
  {
    id: 'productivity',
    name: 'Productivity Report',
    description: 'Team performance and utilization rates',
    icon: BarChart3,
    category: 'operational',
    color: 'cyan'
  }
];

export const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState('month');
  const [generatingReport, setGeneratingReport] = useState(null);
  const [reportData, setReportData] = useState(null);

  const handleGenerateReport = async (reportId) => {
    setGeneratingReport(reportId);
    setSelectedReport(reportId);
    
    try {
      const response = await api.get(`/reports/${reportId}?period=${dateRange}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
      // Generate demo data
      setReportData(generateDemoData(reportId));
    } finally {
      setGeneratingReport(null);
    }
  };

  const generateDemoData = (reportId) => {
    const baseData = {
      generated_at: new Date().toISOString(),
      period: dateRange,
      report_id: reportId
    };

    switch (reportId) {
      case 'financial_summary':
        return {
          ...baseData,
          metrics: {
            totalRevenue: 125750,
            totalExpenses: 45320,
            netIncome: 80430,
            revenueChange: 12.5,
            expenseChange: -3.2
          },
          breakdown: [
            { category: 'Legal Fees', amount: 98500 },
            { category: 'Retainers', amount: 18500 },
            { category: 'Consultation', amount: 8750 }
          ]
        };
      case 'accounts_receivable':
        return {
          ...baseData,
          metrics: {
            totalOutstanding: 32450,
            current: 18500,
            thirtyDays: 8200,
            sixtyDays: 4250,
            ninetyPlus: 1500
          },
          clients: [
            { name: 'Tech Corp', outstanding: 12500, oldest: '45 days' },
            { name: 'Johnson LLC', outstanding: 8500, oldest: '32 days' },
            { name: 'Smith Family Trust', outstanding: 6450, oldest: '28 days' },
            { name: 'Brown Enterprises', outstanding: 5000, oldest: '15 days' }
          ]
        };
      case 'trust_account':
        return {
          ...baseData,
          metrics: {
            totalBalance: 156780,
            clientCount: 24,
            depositsThisPeriod: 45000,
            withdrawalsThisPeriod: 28500
          },
          topBalances: [
            { client: 'Johnson LLC', balance: 45000 },
            { client: 'Tech Corp', balance: 35280 },
            { client: 'Robert Smith', balance: 25000 },
            { client: 'Sarah Miller', balance: 20500 }
          ]
        };
      case 'case_status':
        return {
          ...baseData,
          metrics: {
            totalCases: 48,
            active: 32,
            pending: 8,
            closed: 8
          },
          byPracticeArea: [
            { area: 'Civil Litigation', count: 15 },
            { area: 'Corporate', count: 12 },
            { area: 'Family Law', count: 10 },
            { area: 'Real Estate', count: 6 },
            { area: 'Criminal Defense', count: 5 }
          ]
        };
      case 'time_summary':
        return {
          ...baseData,
          metrics: {
            totalHours: 342.5,
            billableHours: 298.25,
            utilizationRate: 87.1,
            totalBillable: 89475
          },
          byAttorney: [
            { name: 'Sarah Johnson', hours: 145.5, billable: 43650 },
            { name: 'Michael Chen', hours: 98.25, billable: 24562.50 },
            { name: 'Emily Davis', hours: 54.5, billable: 21262.50 }
          ]
        };
      default:
        return {
          ...baseData,
          metrics: {},
          data: []
        };
    }
  };

  const handleExportReport = async (format) => {
    if (!selectedReport || !reportData) return;
    
    const reportName = reportTypes.find(r => r.id === selectedReport)?.name || 'Report';
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${reportName.replace(/\s+/g, '_')}_${timestamp}`;
    
    if (format === 'csv') {
      // Generate CSV
      let csvContent = '';
      
      if (reportData.metrics) {
        csvContent += 'Metric,Value\n';
        Object.entries(reportData.metrics).forEach(([key, value]) => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          csvContent += `"${label}","${typeof value === 'number' ? value.toLocaleString() : value}"\n`;
        });
        csvContent += '\n';
      }
      
      if (reportData.breakdown?.length) {
        csvContent += '\nBreakdown\n';
        const keys = Object.keys(reportData.breakdown[0]);
        csvContent += keys.join(',') + '\n';
        reportData.breakdown.forEach(row => {
          csvContent += keys.map(k => `"${row[k]}"`).join(',') + '\n';
        });
      }
      
      if (reportData.clients?.length) {
        csvContent += '\nClients\n';
        const keys = Object.keys(reportData.clients[0]);
        csvContent += keys.join(',') + '\n';
        reportData.clients.forEach(row => {
          csvContent += keys.map(k => `"${row[k]}"`).join(',') + '\n';
        });
      }
      
      if (reportData.topBalances?.length) {
        csvContent += '\nTop Balances\n';
        const keys = Object.keys(reportData.topBalances[0]);
        csvContent += keys.join(',') + '\n';
        reportData.topBalances.forEach(row => {
          csvContent += keys.map(k => `"${row[k]}"`).join(',') + '\n';
        });
      }
      
      if (reportData.byPracticeArea?.length) {
        csvContent += '\nBy Practice Area\n';
        const keys = Object.keys(reportData.byPracticeArea[0]);
        csvContent += keys.join(',') + '\n';
        reportData.byPracticeArea.forEach(row => {
          csvContent += keys.map(k => `"${row[k]}"`).join(',') + '\n';
        });
      }
      
      if (reportData.byAttorney?.length) {
        csvContent += '\nBy Attorney\n';
        const keys = Object.keys(reportData.byAttorney[0]);
        csvContent += keys.join(',') + '\n';
        reportData.byAttorney.forEach(row => {
          csvContent += keys.map(k => `"${row[k]}"`).join(',') + '\n';
        });
      }
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success('CSV exported successfully');
    } else if (format === 'pdf') {
      // For PDF, we'll generate a printable HTML and trigger print
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to export PDF');
        return;
      }
      
      let metricsHtml = '';
      if (reportData.metrics) {
        metricsHtml = '<h2>Summary Metrics</h2><table border="1" cellpadding="8" style="border-collapse: collapse; width: 100%;">';
        metricsHtml += '<thead><tr><th>Metric</th><th>Value</th></tr></thead><tbody>';
        Object.entries(reportData.metrics).forEach(([key, value]) => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          metricsHtml += `<tr><td>${label}</td><td style="text-align: right;">${typeof value === 'number' ? (key.toLowerCase().includes('rate') ? value.toFixed(1) + '%' : '$' + value.toLocaleString()) : value}</td></tr>`;
        });
        metricsHtml += '</tbody></table>';
      }
      
      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${reportName} - ${timestamp}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
            h2 { color: #475569; margin-top: 30px; }
            table { width: 100%; margin-top: 15px; }
            th { background: #f1f5f9; text-align: left; }
            td, th { padding: 10px; border: 1px solid #e2e8f0; }
            .meta { color: #64748b; font-size: 14px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>${reportName}</h1>
          <p class="meta">Generated: ${new Date().toLocaleString()} | Period: ${dateRange}</p>
          ${metricsHtml}
          <script>window.onload = function() { window.print(); }</script>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success('PDF export opened');
    }
  };

  const handlePrint = () => {
    if (!selectedReport || !reportData) return;
    handleExportReport('pdf');
  };

  const filteredReports = activeTab === 'all' 
    ? reportTypes 
    : reportTypes.filter(r => r.category === activeTab);

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-600',
      amber: 'bg-amber-100 text-amber-600',
      purple: 'bg-purple-100 text-purple-600',
      blue: 'bg-blue-100 text-blue-600',
      orange: 'bg-orange-100 text-orange-600',
      indigo: 'bg-indigo-100 text-indigo-600',
      pink: 'bg-pink-100 text-pink-600',
      cyan: 'bg-cyan-100 text-cyan-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Reports</h1>
          <p className="text-slate-500 mt-1">Generate financial, operational, and compliance reports</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report List */}
        <div className="lg:col-span-1 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="financial">Finance</TabsTrigger>
              <TabsTrigger value="operational">Ops</TabsTrigger>
              <TabsTrigger value="compliance">Comply</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-3">
            {filteredReports.map((report) => (
              <Card 
                key={report.id}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  selectedReport === report.id && "ring-2 ring-blue-500"
                )}
                onClick={() => handleGenerateReport(report.id)}
                data-testid={`report-${report.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={cn("p-2 rounded-lg", getColorClasses(report.color))}>
                      <report.icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900">{report.name}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{report.description}</p>
                    </div>
                    {generatingReport === report.id && (
                      <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Report Preview */}
        <Card className="lg:col-span-2" data-testid="report-preview">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {selectedReport 
                    ? reportTypes.find(r => r.id === selectedReport)?.name 
                    : 'Select a Report'}
                </CardTitle>
                <CardDescription>
                  {reportData?.generated_at 
                    ? `Generated ${formatDate(reportData.generated_at)}` 
                    : 'Click a report to generate'}
                </CardDescription>
              </div>
              {selectedReport && reportData && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExportReport('pdf')} data-testid="export-pdf-btn">
                    <FileText className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportReport('csv')} data-testid="export-csv-btn">
                    <FileSpreadsheet className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedReport ? (
              <div className="text-center py-16 text-slate-500">
                <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <p className="text-lg font-medium">No Report Selected</p>
                <p className="text-sm mt-1">Choose a report from the list to generate</p>
              </div>
            ) : generatingReport ? (
              <div className="text-center py-16">
                <RefreshCw className="h-12 w-12 mx-auto animate-spin text-blue-500 mb-4" />
                <p className="text-slate-600">Generating report...</p>
              </div>
            ) : reportData ? (
              <div className="space-y-6">
                {/* Metrics */}
                {reportData.metrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(reportData.metrics).slice(0, 4).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-lg bg-slate-50">
                        <p className="text-sm text-slate-500 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p className="text-xl font-bold text-slate-900 mt-1">
                          {typeof value === 'number' 
                            ? (key.toLowerCase().includes('rate') || key.toLowerCase().includes('change')
                              ? `${value}%`
                              : key.toLowerCase().includes('hours') || key.toLowerCase().includes('count') || 
                                key.toLowerCase().includes('cases') || key.toLowerCase().includes('active') ||
                                key.toLowerCase().includes('pending') || key.toLowerCase().includes('closed') ||
                                key.toLowerCase().includes('clients')
                                ? value
                                : formatCurrency(value))
                            : value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Breakdown/Data */}
                {(reportData.breakdown || reportData.clients || reportData.topBalances || reportData.byPracticeArea || reportData.byAttorney) && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          {selectedReport === 'financial_summary' && (
                            <>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Category</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
                            </>
                          )}
                          {selectedReport === 'accounts_receivable' && (
                            <>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Client</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Outstanding</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Oldest</th>
                            </>
                          )}
                          {selectedReport === 'trust_account' && (
                            <>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Client</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Balance</th>
                            </>
                          )}
                          {selectedReport === 'case_status' && (
                            <>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Practice Area</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Cases</th>
                            </>
                          )}
                          {selectedReport === 'time_summary' && (
                            <>
                              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Attorney</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Hours</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Billable</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {selectedReport === 'financial_summary' && reportData.breakdown?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{item.category}</td>
                            <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(item.amount)}</td>
                          </tr>
                        ))}
                        {selectedReport === 'accounts_receivable' && reportData.clients?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{item.name}</td>
                            <td className="py-3 px-4 text-right text-amber-600 font-semibold">{formatCurrency(item.outstanding)}</td>
                            <td className="py-3 px-4 text-right text-slate-500">{item.oldest}</td>
                          </tr>
                        ))}
                        {selectedReport === 'trust_account' && reportData.topBalances?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{item.client}</td>
                            <td className="py-3 px-4 text-right text-purple-600 font-semibold">{formatCurrency(item.balance)}</td>
                          </tr>
                        ))}
                        {selectedReport === 'case_status' && reportData.byPracticeArea?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{item.area}</td>
                            <td className="py-3 px-4 text-right text-blue-600 font-semibold">{item.count}</td>
                          </tr>
                        ))}
                        {selectedReport === 'time_summary' && reportData.byAttorney?.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium text-slate-900">{item.name}</td>
                            <td className="py-3 px-4 text-right text-slate-600">{item.hours}h</td>
                            <td className="py-3 px-4 text-right text-green-600 font-semibold">{formatCurrency(item.billable)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
